/*jshint esversion: 11 */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Table,
  Label,
  FormGroup,
  Button,
  Alert,
  Input,
  Spinner,
  CustomInput
} from 'reactstrap';
import { connect } from 'react-redux';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import moment from 'moment';
import { validatePermissionKey } from '../../helpers/permission';
import {
  CREATE_WEEKLY_ACTION_RECORD,
  UPDATE_WEEKLY_ACTION_RECORD,
  PUBLISH_WEEKLY_ACTION_RECORD
} from '../Utility/PermissionKeys';
import { useParams } from 'react-router-dom';
import { getBatchInfo } from '../../api/batch';
import { getWeeklyActionRecordTemplateByOfferingId } from '../../api/weeklyActionRecordTemplate';
import {
  getWeeklyActionRecord,
  bulkCreateWeeklyActionRecord,
  bulkUpdateWeeklyActionRecord
} from '../../api/weeklyActionRecord';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useHistory } from 'react-router-dom';


const RecordView = ({ record, onEditClick, authUser }) => {
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];
  const dayLabels = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat'
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex">
          <h5>Weekly Action Record</h5>
          {validatePermissionKey(authUser, PUBLISH_WEEKLY_ACTION_RECORD) && (
            <div className="d-flex justify-content-end mb-3 ml-2">
              <span
                className={`badge badge-pill p-2 ${record.is_published
                    ? 'badge-soft-primary'
                    : 'badge-soft-danger'
                  }`}
              >
                Status: {record.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
          )}
        </div>
        <div>
          {validatePermissionKey(authUser, UPDATE_WEEKLY_ACTION_RECORD) && (
            <Button color="warning" className="mr-2" onClick={onEditClick}>
              <i className="ri-edit-line align-bottom me-1"></i> Edit
            </Button>
          )}
        </div>
      </div>

      <h6 className="mt-4">Daily Activities</h6>
      <div className="table-responsive">
        <Table bordered>
          <thead>
            <tr>
              <th>Activity</th>
              {daysOfWeek.map((day) => (
                <th key={day}>{dayLabels[day]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {record.daily_activities?.map((activity, idx) => (
              <tr key={idx}>
                <td>{activity.activity}</td>
                {daysOfWeek.map((day) => (
                  <td key={`${day}-${idx}`}>
                    {activity.activity_execution[day] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <h6 className="mt-4">Weekly Activities</h6>
      <Table bordered>
        <thead>
          <tr>
            <th>Activity</th>
            <th>Work Done</th>
          </tr>
        </thead>
        <tbody>
          {record.weekly_activities?.map((activity, idx) => (
            <tr key={idx}>
              <td>{activity.activity}</td>
              <td>{activity.activity_execution || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Separate component for creating/editing records
const RecordForm = ({
  mode,
  template,
  formData,
  onFieldChange,
  onPublishChange,
  onSave,
  onCancel,
  saving,
  startDate,
  endDate,
  authUser
}) => {
  const daysOfWeek = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
  ];
  const dayLabels = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat'
  };

  const handleDailyChange = (index, day, value) => {
    onFieldChange('daily_activities', index, day, value);
  };

  const handleWeeklyChange = (index, value) => {
    onFieldChange('weekly_activities', index, null, value);
  };

  return (
    <div className="mt-4">
      <h5>
        {mode === 'create' ? 'Create New Weekly Record' : 'Edit Weekly Record'}
        <span className="text-muted ml-2" style={{ fontSize: '0.9rem' }}>
          ({moment(startDate).format('MMM DD')} -{' '}
          {moment(endDate).format('MMM DD')})
        </span>
      </h5>

      {!template ? (
        <Alert color="danger" className="mt-3">
          No template found for this offering
        </Alert>
      ) : (
        <>
          <h6 className="mt-4">Daily Activities</h6>
          <div className="table-responsive">
            <Table bordered>
              <thead>
                <tr>
                  <th>Activity</th>
                  {daysOfWeek.map((day) => (
                    <th key={day}>{dayLabels[day]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formData.daily_activities.map((activity, idx) => (
                  <tr key={idx}>
                    <td>{activity.activity}</td>
                    {daysOfWeek.map((day) => (
                      <td key={`${day}-${idx}`}>
                        <Input
                          type="text"
                          value={activity.activity_execution[day] || ''}
                          onChange={(e) =>
                            handleDailyChange(idx, day, e.target.value)
                          }
                          placeholder={`${dayLabels[day]} execution`}
                          disabled={saving}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <h6 className="mt-4">Weekly Activities</h6>
          <Table bordered>
            <thead>
              <tr>
                <th>Activity</th>
                <th>Execution</th>
              </tr>
            </thead>
            <tbody>
              {formData.weekly_activities.map((activity, idx) => (
                <tr key={idx}>
                  <td>{activity.activity}</td>
                  <td>
                    <Input
                      type="text"
                      value={activity.activity_execution || ''}
                      onChange={(e) => handleWeeklyChange(idx, e.target.value)}
                      placeholder="Activity execution"
                      disabled={saving}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end mt-4">
            <Button
              color="danger"
              className="mr-2"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="secondary"
              className="mr-2"
              onClick={() => onSave(false)}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                'Save Record'
              )}
            </Button>
            {validatePermissionKey(authUser, PUBLISH_WEEKLY_ACTION_RECORD) && (
              <Button
                color="primary"
                onClick={() => onSave(true)}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner size="sm" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  'Publish Record'
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const WeeklyActionRecord = ({ authUser }) => {

  const history = useHistory();

  const batchId = history.location.state?.batchId;

  const [breadcrumbItems] = useState([
    { title: 'Weekly Action Records', link: '#' },
    { title: 'Manage Records', link: '#' }
  ]);
  const [batchLoading, setBatchLoading] = useState(true);
  const [recordLoading, setRecordLoading] = useState(false);
  const [batch, setBatch] = useState(null);
  const [offering, setOffering] = useState(null);
  const [template, setTemplate] = useState(null);
  const [record, setRecord] = useState(null);
  // const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  // const [endDate, setEndDate] = useState(moment().add(6, 'days').format('YYYY-MM-DD'));
  const [mode, setMode] = useState('view'); // 'view', 'edit', 'create'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    daily_activities: [],
    weekly_activities: [],
    is_published: false
  });
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    message: ''
  });

  const getMonday = (date) => {
    return date.clone().startOf('isoWeek');
  };

  const initialMonday = getMonday(moment()).format('YYYY-MM-DD');
  const initialSunday = moment(initialMonday)
    .add(6, 'days')
    .format('YYYY-MM-DD');

  const [startDate, setStartDate] = useState(initialMonday);
  const [endDate, setEndDate] = useState(initialSunday);
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    if (batchId) {
      handleLoadRecord();
    }
  }, [batchId, startDate, endDate]);

  const handleDateChange = (e) => {
    const selectedDate = moment(e.target.value);
    const dayOfWeek = selectedDate.day();

    if (dayOfWeek !== 1) {
      // Use moment.js to get the Monday
      const monday = getMonday(selectedDate).format('YYYY-MM-DD');
      setDateError(
        'Only Mondays can be selected. Adjusted to ' +
        moment(monday).format('MMM DD')
      );
      setStartDate(monday);
      setEndDate(moment(monday).add(6, 'days').format('YYYY-MM-DD'));
    } else {
      setDateError(null);
      const newStartDate = e.target.value;
      setStartDate(newStartDate);
      setEndDate(moment(newStartDate).add(6, 'days').format('YYYY-MM-DD'));
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setBatchLoading(true);
        const batchData = await getBatchInfo(batchId);
        setBatch(batchData);
        setOffering(batchData.offering);
        const templateData = await getWeeklyActionRecordTemplateByOfferingId(
          batchData.offering._id
        );
        setTemplate(templateData[0]);
      } catch (error) {
        setError("You don't have VIEW_BATCH permission");
      } finally {
        setBatchLoading(false);
      }
    };

    if (batchId) {
      loadInitialData();
    }
  }, [batchId]);

  const initializeFormFromTemplate = () => {
    if (!template) return;

    const dailyActivities = template.information
      .filter((item) => item.type === 'daily')
      .map((item) => ({
        activity: item.activity,
        activity_execution: {
          monday: '',
          tuesday: '',
          wednesday: '',
          thursday: '',
          friday: '',
          saturday: ''
        }
      }));

    const weeklyActivities = template.information
      .filter((item) => item.type === 'weekly')
      .map((item) => ({
        activity: item.activity,
        activity_execution: ''
      }));

    setFormData({
      daily_activities: dailyActivities,
      weekly_activities: weeklyActivities,
      is_published: false // Default to false for new records
    });
  };

  // const handleDateChange = (e) => {
  //     const newStartDate = e.target.value;
  //     setStartDate(newStartDate);
  //     setEndDate(moment(newStartDate).add(6, 'days').format('YYYY-MM-DD'));
  // };

  const handleLoadRecord = async () => {
    setRecordLoading(true);
    setError(null);
    try {
      const recordData = await getWeeklyActionRecord(
        startDate,
        endDate,
        batchId
      );
      setRecord(recordData);
      if (recordData) {
        setFormData({
          daily_activities: recordData.daily_activities || [],
          weekly_activities: recordData.weekly_activities || [],
          is_published: recordData.is_published || false
        });
      }
      setMode('view');
    } catch (error) {
      setError('Failed to load weekly record: ' + error.message);
    } finally {
      setRecordLoading(false);
    }
  };

  const handleCreateNew = () => {
    setError(null);
    if (!template) {
      setError('No template found for this offering');
      return;
    }
    setRecord(null);
    setMode('create');
    initializeFormFromTemplate();
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleCancel = () => {
    if (record) {
      setFormData({
        daily_activities: record.daily_activities || [],
        weekly_activities: record.weekly_activities || [],
        is_published: record.is_published || false
      });
    }
    setMode('view');
  };

  // FIXED: Proper immutable state update
  const handleFieldChange = (type, index, field, value) => {
    setFormData((prevFormData) => {
      // Create a new copy of the form data
      const newFormData = { ...prevFormData };

      if (type === 'daily_activities') {
        // For daily activities - update specific day
        newFormData.daily_activities = [...prevFormData.daily_activities];
        newFormData.daily_activities[index] = {
          ...newFormData.daily_activities[index],
          activity_execution: {
            ...newFormData.daily_activities[index].activity_execution,
            [field]: value
          }
        };
      } else if (type === 'weekly_activities') {
        // For weekly activities - update entire execution value
        newFormData.weekly_activities = [...prevFormData.weekly_activities];
        newFormData.weekly_activities[index] = {
          ...newFormData.weekly_activities[index],
          activity_execution: value
        };
      }

      return newFormData;
    });
  };

  // Handle publish state change
  const handlePublishChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      is_published: value
    }));
  };

  const handleSave = async (isPublished) => {
    setSaving(true);
    setError(null);

    try {
      const data = {
        batch_id: batchId,
        daily_activities: formData.daily_activities,
        weekly_activities: formData.weekly_activities,
        start_date: startDate,
        end_date: endDate,
        is_published: isPublished,
        offering: batch?.offering._id,
      };

      let result;
      if (mode === 'edit' && record) {
        result = await bulkUpdateWeeklyActionRecord(data, record._id);
      } else {
        result = await bulkCreateWeeklyActionRecord(data);
      }

      if (result?.error) {
        setError(result.error);
      } else {
        const newRecord = {
          ...data,
          _id: result?.response?.data?._id || record?._id
        };
        setRecord(newRecord);
        setMode('view');
      }
      setSuccessAlert({ show: true, message: 'Record updated successfully' });
    } catch (error) {
      setError('Failed to save record: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4">
      {successAlert.show && (
        <SweetAlert
          success
          title="Updated"
          onConfirm={() => setSuccessAlert({ show: false, message: '' })}
        >
          {successAlert.message}
        </SweetAlert>
      )}
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                {batchLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading batch and template...</p>
                  </div>
                ) : (
                  <>
                    <Row className="mb-4">
                      {error && (
                        <Col md={12}>
                          <Alert color="danger">{error}</Alert>
                        </Col>
                      )}
                      {/* <Col md={4}>
                        <FormGroup>
                          <Label>Batch</Label>
                          <Input
                            type="text"
                            value={batch?.name || ''}
                            readOnly
                            disabled
                          />
                        </FormGroup>
                      </Col> */}
                      <Col md={6}>
                        <FormGroup>
                          <Label>Offering</Label>
                          <Input
                            type="text"
                            value={offering?.name || ''}
                            readOnly
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      {/* <Col md={2}>
                                                <FormGroup>
                                                    <Label>Start Date</Label>
                                                    <Input
                                                        type="date"
                                                        value={startDate}
                                                        onChange={handleDateChange}
                                                        disabled={mode !== 'view'}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={2}>
                                                <FormGroup>
                                                    <Label>End Date</Label>
                                                    <Input
                                                        type="date"
                                                        value={endDate}
                                                        readOnly
                                                        disabled
                                                    />
                                                </FormGroup>
                                            </Col> */}
                      <Col md={6}>
                        <FormGroup>
                          <Label>Week Start (Monday)</Label>
                          <Input
                            type="date"
                            value={startDate}
                            onChange={handleDateChange}
                            disabled={mode !== 'view'}
                          />
                          {dateError && (
                            <small className="text-danger mr-2">
                              {dateError}
                            </small>
                          )}
                          <small className="text-muted">
                            Week: {moment(startDate).format('MMM DD')} -{' '}
                            {moment(endDate).format('MMM DD')}
                          </small>
                        </FormGroup>
                      </Col>
                      {/* <Col md={2} className="d-flex align-items-center pb-2">
                        <Button
                          color="primary"
                          onClick={handleLoadRecord}
                          disabled={mode !== 'view' || saving || recordLoading}
                        >
                          {recordLoading ? (
                            <>
                              <Spinner size="sm" className="mr-2" />
                              Loading...
                            </>
                          ) : (
                            'Load Record'
                          )}
                        </Button>
                      </Col> */}
                    </Row>

                    {recordLoading && (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading record...</p>
                      </div>
                    )}

                    {!recordLoading && mode === 'view' && record && (
                      <RecordView
                        record={record}
                        onEditClick={handleEdit}
                        authUser={authUser}
                      />
                    )}

                    {(mode === 'edit' || mode === 'create') && (
                      <RecordForm
                        mode={mode}
                        template={template}
                        formData={formData}
                        onFieldChange={handleFieldChange}
                        onPublishChange={handlePublishChange}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        saving={saving}
                        startDate={startDate}
                        endDate={endDate}
                        authUser={authUser}
                      />
                    )}

                    {!recordLoading && mode === 'view' && !record && (
                      <div className="text-center mt-5 py-5">
                        <h5>No record loaded</h5>
                        <p className="text-muted">
                          Select a date range and click "Load Record" to view
                          existing records
                        </p>
                        <p className="text-muted mb-4">
                          Start date: {moment(startDate).format('MMM DD, YYYY')}{' '}
                          - End date: {moment(endDate).format('MMM DD, YYYY')}
                        </p>
                        {validatePermissionKey(
                          authUser,
                          CREATE_WEEKLY_ACTION_RECORD
                        ) && (
                            <Button
                              color="success"
                              className="mt-3"
                              onClick={handleCreateNew}
                            >
                              <i className="ri-add-line align-middle mr-2"></i>
                              Create New Record
                            </Button>
                          )}
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    authUser: state.Login?.user
  };
};

export default connect(mapStateToProps)(WeeklyActionRecord);
